import { fetchApi } from './api';

export type CoverageStatus = 'OK' | 'BELOW_SAFETY' | 'STOCKOUT';
export type SimulationType = 'WITHOUT_SUGGESTED_ORDER' | 'WITH_SUGGESTED_ORDER';

/** DRF serializa DecimalField como string; los tratamos como string|number. */
export type Decimalish = string | number | null;

export interface InventorySimulationRunMeta {
  id: number;
  calculation_date: string;
  horizon_start: string;
  horizon_end: string;
  granularity: string;
}

export interface CoverageProductRow {
  product_result_id: number;
  product_id: number;
  product_code: string;
  product_name: string;
  family_id: number | null;
  family_name: string | null;
  warehouse_id: number | null;
  warehouse_code: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  is_primary: boolean;
  coverage_status: CoverageStatus;
  first_below_safety_date: string | null;
  first_stockout_date: string | null;
  days_of_supply: Decimalish;
  final_suggested_quantity: Decimalish;
  lead_time_days: number | null;
  review_period_days: number | null;
  safety_stock: Decimalish;
  internal_safety_stock: Decimalish;
  supplier_safety_stock: Decimalish;
  has_projection: boolean;
}

export interface CoverageGroupRow {
  group_key: string;
  product_id: number;
  product_code: string;
  product_name: string;
  family_id: number | null;
  family_name: string | null;
  warehouse_id: number | null;
  warehouse_code: string | null;
  default_product_result_id: number;
  suppliers: CoverageProductRow[];
}

export interface CoverageKpis {
  covered: number;
  below_safety: number;
  stockout: number;
  total: number;
}

export interface InventorySimulationPagination {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface InventorySimulationProductsResponse {
  run: InventorySimulationRunMeta | null;
  results: CoverageGroupRow[];
  coverage_kpis: CoverageKpis;
  pagination: InventorySimulationPagination;
  filters_applied: Record<string, string | null>;
}

export interface IdName {
  id: number;
  name: string;
}

export interface InventorySimulationFiltersResponse {
  run: InventorySimulationRunMeta | null;
  warehouses: IdName[];
  suppliers: IdName[];
  families: IdName[];
  products: { id: number; code: string; name: string }[];
  coverage_statuses: { id: CoverageStatus; name: string }[];
}

export interface ReceiptDetail {
  type?: string;
  quantity?: Decimalish;
  expected_date?: string;
  purchase_order_id?: number;
  purchase_order_item_id?: number;
}

export interface ProjectionRow {
  period_index: number;
  period_start: string;
  period_end: string;
  simulation_type: SimulationType;
  starting_stock: Decimalish;
  confirmed_receipts: Decimalish;
  suggested_receipts: Decimalish;
  projected_demand: Decimalish;
  ending_stock: Decimalish;
  safety_stock: Decimalish;
  below_safety_stock: boolean;
  stockout: boolean;
  demand_source: string;
  receipts_details?: ReceiptDetail[];
}

export interface ProjectionContext {
  id: number;
  product_id: number;
  product_code_snapshot: string;
  product_name_snapshot: string;
  warehouse_id: number | null;
  warehouse_code_snapshot: string;
  supplier_id: number | null;
  supplier_name_snapshot: string;
  calculation_method: string;
  coverage_status: CoverageStatus;
  lead_time_days: number | null;
  lead_time_weeks: Decimalish;
  review_period_days: number | null;
  protection_period_days: number | null;
  safety_stock: Decimalish;
  internal_safety_stock: Decimalish;
  supplier_safety_stock: Decimalish;
  reorder_point: Decimalish;
  min_stock: Decimalish;
  max_stock: Decimalish;
  target_stock: Decimalish;
  days_of_supply: Decimalish;
  raw_suggested_quantity: Decimalish;
  final_suggested_quantity: Decimalish;
  first_below_safety_date: string | null;
  first_stockout_date: string | null;
  minimum_projected_stock: Decimalish;
  minimum_projected_stock_date: string | null;
  solves_stockout_with_suggested_order: boolean | null;
  below_safety_after_suggested_order: boolean | null;
  creates_excess_stock: boolean | null;
  simulation_summary: Record<string, unknown>;
}

export interface InventorySimulationProjectionResponse {
  run: InventorySimulationRunMeta | null;
  context: ProjectionContext | null;
  projections: ProjectionRow[];
}

export interface CoverageProductsParams {
  warehouse_id?: number | null;
  supplier_id?: number | string | null;
  family_id?: number | string | null;
  product_id?: number | string | null;
  coverage_status?: CoverageStatus | string | null;
  search?: string | null;
  sort?: string | null;
  direction?: 'asc' | 'desc' | null;
  page?: number;
  page_size?: number;
}

const BASE = 'replenishment/inventory-simulation';

function buildParams(input: Record<string, unknown>): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = typeof value === 'number' ? value : String(value);
  });
  return params;
}

class InventorySimulationService {
  async getFilters(request?: { signal?: AbortSignal }): Promise<InventorySimulationFiltersResponse> {
    return fetchApi<InventorySimulationFiltersResponse>(`${BASE}/filters/`, { signal: request?.signal });
  }

  async getProducts(
    params: CoverageProductsParams,
    request?: { signal?: AbortSignal },
  ): Promise<InventorySimulationProductsResponse> {
    return fetchApi<InventorySimulationProductsResponse>(`${BASE}/products/`, {
      params: buildParams(params as Record<string, unknown>),
      signal: request?.signal,
    });
  }

  async getProjection(
    params: {
      product_result_id?: number | null;
      product_id?: number | null;
      warehouse_id?: number | null;
      supplier_id?: number | null;
      simulation_type?: SimulationType;
    },
    request?: { signal?: AbortSignal },
  ): Promise<InventorySimulationProjectionResponse> {
    return fetchApi<InventorySimulationProjectionResponse>(`${BASE}/projection/`, {
      params: buildParams(params as Record<string, unknown>),
      signal: request?.signal,
    });
  }
}

export const inventorySimulationService = new InventorySimulationService();
